import { useState, useEffect, useRef, useCallback } from "react"
import { collection, addDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../firebase-config"
import matchaLogo from "../assets/images/matcha-logo.svg"
import "./Playtest.css"

// Simple shared gate — this is a playtest, not real security (the value ships
// in the client bundle). Change it here, or pass ?key=... in the URL.
const PLAYTEST_KEY = "matcha-roam"

const POOL = ["🍵", "🌿", "🍡", "🫖", "🍙", "🎋", "🐸", "🌸", "🍃", "🧋", "🥟", "🎐"]
const MAX_CELL = 84
const MIN_CELL = 56
const DESKTOP_GAP = 12
const MOBILE_GAP = 8

const computeCellSize = (cols) => {
  if (typeof window === "undefined") return { cell: MAX_CELL, gap: DESKTOP_GAP }
  const vw = window.innerWidth
  const pagePad = vw < 480 ? 24 : 32
  const gap = vw < 480 ? MOBILE_GAP : DESKTOP_GAP
  const available = Math.min(vw - pagePad, 560)
  const raw = Math.floor((available - (cols - 1) * gap) / cols)
  const cell = Math.max(MIN_CELL, Math.min(MAX_CELL, raw))
  return { cell, gap }
}

// ---- Mechanic catalogue ----
// Each: id, name, kind (modifier|booster|combo), blurb (card subtitle),
// info (popup body), cfg (LabBoard config).
const MECHANICS = [
  // Modifiers — hostile, the difficulty/variety treadmill
  {
    id: "roaming", name: "Roaming Tiles", kind: "modifier", blurb: "Tiles slide to new spots as you play.",
    info: "After you memorize the board, pairs of face-down tiles light up and slide to swap positions every few seconds. You have to track the movement live, not just trust the layout you memorized.",
    cfg: { rows: 4, cols: 4, memorizeMs: 2800, roaming: 4000 },
  },
  {
    id: "burst", name: "Quake", kind: "modifier", blurb: "Every wrong guess scrambles tiles.",
    info: "Miss a match and the board punishes you — a few unmatched tiles light up and shuffle positions. Accuracy matters more than speed; reckless flipping wrecks your mental map.",
    cfg: { rows: 4, cols: 4, memorizeMs: 2800, burstOnMismatch: 3 },
  },
  // Objectives — what "winning" means
  {
    id: "targetSet", name: "Target Set", kind: "objective", blurb: "Only the highlighted pairs matter.",
    info: "You don't have to clear the whole board — just find the target pairs shown up top. The rest are distractors. You win the moment all the targets are matched. Tests focus and prioritizing what to remember.",
    cfg: { rows: 4, cols: 4, memorizeMs: 2500, targets: 3 },
  },
]
const byId = Object.fromEntries(MECHANICS.map((m) => [m.id, m]))

const shuffle = (arr) => {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const buildDeck = (cfg) => {
  const cells = cfg.rows * cfg.cols
  const decoy = !!cfg.decoy
  const pairCount = decoy ? Math.floor((cells - 1) / 2) : cells / 2
  const vals = shuffle(POOL).slice(0, pairCount)
  const cards = []
  let id = 0
  vals.forEach((v) => {
    cards.push({ id: id++, value: v, memorizeValue: v })
    cards.push({ id: id++, value: v, memorizeValue: v })
  })
  if (decoy) {
    const orphan = shuffle(POOL.filter((x) => !vals.includes(x)))[0] || "❓"
    cards.push({ id: id++, value: orphan, memorizeValue: orphan, isDecoy: true })
  }
  if (cfg.faceChange) {
    for (let p = 0; p < cfg.faceChange; p++) {
      const v = vals[p]
      const fake = shuffle(POOL.filter((x) => x !== v))[0]
      cards.filter((c) => c.value === v).forEach((c) => { c.isCham = true; c.memorizeValue = fake })
    }
  }
  const targetValues = cfg.targets ? vals.slice(0, cfg.targets) : []
  const order = shuffle(cards.map((c) => c.id))
  const slots = {}
  order.forEach((cid, slot) => (slots[slot] = cid))
  return { cards, slots, matchable: cards.length - (decoy ? 1 : 0), targetValues }
}

function LabBoard({ cfg, onDone }) {
  const [{ cell: CELL, gap: GAP }, setDims] = useState(() => computeCellSize(cfg.cols))
  useEffect(() => {
    const onResize = () => setDims(computeCellSize(cfg.cols))
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [cfg.cols])
  const xy = (slot) => ({ left: (slot % cfg.cols) * (CELL + GAP), top: Math.floor(slot / cfg.cols) * (CELL + GAP) })
  const [deck, setDeck] = useState(() => buildDeck(cfg))
  const [slots, setSlots] = useState(deck.slots)
  const [phase, setPhase] = useState("memorize") // memorize | play | won | lost
  const [flipped, setFlipped] = useState([])
  const [matched, setMatched] = useState(new Set())
  const [swapping, setSwapping] = useState([])
  const [unmatched, setUnmatched] = useState(new Set())
  const [ghosts, setGhosts] = useState([]) // cardIds showing a fading hint
  const [revealAll, setRevealAll] = useState(false)
  const [uses, setUses] = useState(cfg.uses || 0)
  const [timeLeft, setTimeLeft] = useState(cfg.timed || 0)
  const [moves, setMoves] = useState(0)
  const [startTs, setStartTs] = useState(null)
  const [, force] = useState(0)
  const lockRef = useRef(false)
  const slotsRef = useRef(slots)
  const matchedRef = useRef(matched)
  const flippedRef = useRef(flipped)
  useEffect(() => { slotsRef.current = slots }, [slots])
  useEffect(() => { matchedRef.current = matched }, [matched])
  useEffect(() => { flippedRef.current = flipped }, [flipped])

  const cardById = (id) => deck.cards.find((c) => c.id === id)

  // memorize -> play
  useEffect(() => {
    if (phase !== "memorize") return
    const t = setTimeout(() => { setPhase("play"); setStartTs(Date.now()) }, cfg.memorizeMs)
    return () => clearTimeout(t)
  }, [phase, cfg.memorizeMs])

  // play clock (for elapsed display + timed countdown)
  useEffect(() => {
    if (phase !== "play") return
    const i = setInterval(() => {
      force((n) => n + 1)
      if (cfg.timed) {
        setTimeLeft((t) => {
          if (t <= 0.1) { setPhase("lost"); return 0 }
          return +(t - 0.1).toFixed(1)
        })
      }
    }, 100)
    return () => clearInterval(i)
  }, [phase, cfg.timed])

  // roaming swaps
  useEffect(() => {
    if (phase !== "play" || !cfg.roaming) return
    const tick = setInterval(() => doSwaps(1), cfg.roaming)
    return () => clearInterval(tick)
  }, [phase, cfg.roaming]) // eslint-disable-line

  const doSwaps = (count) => {
    const prev = slotsRef.current
    const m = matchedRef.current
    const f = flippedRef.current
    const eligible = Object.keys(prev).map(Number).filter((s) => !m.has(prev[s]) && !f.includes(prev[s]))
    if (eligible.length < 2) return
    const tele = []
    let next = { ...prev }
    const pool = shuffle(eligible)
    for (let k = 0; k < count && pool.length >= 2; k++) {
      const a = pool.pop(), b = pool.pop()
      tele.push(next[a], next[b])
      const tmp = next[a]; next[a] = next[b]; next[b] = tmp
    }
    setSwapping(tele)
    setTimeout(() => setSwapping([]), 650)
    setSlots(next)
  }

  const isWin = (m) =>
    cfg.targets
      ? deck.targetValues.every((v) => deck.cards.filter((c) => c.value === v).every((c) => m.has(c.id)))
      : m.size >= deck.matchable
  const checkWin = (m) => {
    if (isWin(m)) {
      setPhase("won")
      onDone({ result: "won", moves: moves + 1, time: startTs ? (Date.now() - startTs) / 1000 : 0 })
    }
  }

  const handleFlip = (cardId) => {
    if (phase !== "play" || lockRef.current) return
    if (matched.has(cardId) || flipped.includes(cardId)) return
    const next = [...flipped, cardId]
    setFlipped(next)
    if (next.length === 2) {
      setMoves((mv) => mv + 1)
      lockRef.current = true
      const [c1, c2] = next.map(cardById)
      const isMatch = c1.value === c2.value && !c1.isDecoy && !c2.isDecoy
      if (isMatch) {
        setTimeout(() => {
          setMatched((prev) => { const s = new Set(prev); s.add(c1.id); s.add(c2.id); checkWin(s); return s })
          setFlipped([]); lockRef.current = false
        }, 420)
      } else {
        setUnmatched(new Set([c1.id, c2.id]))
        setTimeout(() => {
          setUnmatched(new Set())
          setFlipped([]); lockRef.current = false
          if (cfg.ghost) {
            setGhosts((g) => [...g, c1.id, c2.id])
            setTimeout(() => setGhosts((g) => g.filter((id) => id !== c1.id && id !== c2.id)), 1500)
          }
          if (cfg.burstOnMismatch) doSwaps(cfg.burstOnMismatch)
        }, 800)
      }
    }
  }

  // boosters
  const usePeek = () => { if (uses <= 0) return; setUses((u) => u - 1); setRevealAll(true); setTimeout(() => setRevealAll(false), 1500) }
  const useClock = () => { if (uses <= 0) return; setUses((u) => u - 1); setTimeLeft((t) => +(t + 10).toFixed(1)) }
  const useJoker = () => {
    if (uses <= 0) return
    const unmatched = deck.cards.filter((c) => !matched.has(c.id) && !c.isDecoy)
    const target = unmatched.find((c) => unmatched.filter((x) => x.value === c.value).length >= 2)
    if (!target) return
    const pair = unmatched.filter((c) => c.value === target.value).slice(0, 2)
    setUses((u) => u - 1)
    setMatched((prev) => { const s = new Set(prev); pair.forEach((c) => s.add(c.id)); checkWin(s); return s })
  }

  const restart = useCallback(() => {
    const d = buildDeck(cfg)
    setDeck(d); setSlots(d.slots); setPhase("memorize"); setFlipped([]); setMatched(new Set())
    setSwapping([]); setUnmatched(new Set()); setGhosts([]); setRevealAll(false); setUses(cfg.uses || 0); setTimeLeft(cfg.timed || 0)
    setMoves(0); setStartTs(null); lockRef.current = false
  }, [cfg])

  const elapsed = startTs && phase === "play" ? (Date.now() - startTs) / 1000 : 0
  const boardW = cfg.cols * CELL + (cfg.cols - 1) * GAP
  const boardH = cfg.rows * CELL + (cfg.rows - 1) * GAP

  return (
    <div className="pt-game">
      <div className="pt-hud">
        <span>moves <b>{moves}</b></span>
        {cfg.timed ? (
          <span className={timeLeft < 8 ? "pt-time-low" : ""}>{timeLeft.toFixed(1)}s</span>
        ) : (
          <span>{phase === "memorize" ? "memorize!" : `${elapsed.toFixed(1)}s`}</span>
        )}
        <button className="pt-btn pt-btn-ghost" onClick={restart}>restart</button>
      </div>

      {cfg.booster && phase === "play" && (
        <div className="pt-boosters">
          {cfg.booster === "peek" && <button className="pt-btn pt-btn-booster" onClick={usePeek} disabled={uses <= 0}>👁 Peek ({uses})</button>}
          {cfg.booster === "clock" && <button className="pt-btn pt-btn-booster" onClick={useClock} disabled={uses <= 0}>⏱ +10s ({uses})</button>}
          {cfg.booster === "joker" && <button className="pt-btn pt-btn-booster" onClick={useJoker} disabled={uses <= 0}>🃏 Auto-Match ({uses})</button>}
        </div>
      )}

      {cfg.targets && (
        <div className="pt-targets">
          <span>🎯 Find:</span>
          {deck.targetValues.map((v) => {
            const done = deck.cards.filter((c) => c.value === v).every((c) => matched.has(c.id))
            return <span key={v} className={`pt-target ${done ? "done" : ""}`}>{v}</span>
          })}
        </div>
      )}

      <div className="pt-board" style={{ width: boardW, height: boardH }}>
        {Object.entries(slots).map(([slot, cardId]) => {
          const card = cardById(cardId)
          if (!card) return null
          const isUp = phase === "memorize" || revealAll || flipped.includes(cardId) || matched.has(cardId)
          const face = phase === "memorize" ? card.memorizeValue : card.value
          const pos = xy(Number(slot))
          const isGhost = ghosts.includes(cardId)
          const isMatched = matched.has(cardId)
          const isUnmatched = unmatched.has(cardId)
          return (
            <button
              key={cardId}
              className={`pt-card ${isUp ? "up" : ""} ${isMatched ? "matched" : ""} ${isUnmatched ? "unmatched" : ""} ${swapping.includes(cardId) ? "swapping" : ""} ${isGhost ? "ghost" : ""}`}
              style={{ width: CELL, height: CELL, transform: `translate(${pos.left}px, ${pos.top}px)` }}
              data-cid={cardId}
              onClick={() => handleFlip(cardId)}
            >
              <div className="pt-card-inner">
                <div className="pt-card-front">
                  <img src={matchaLogo} alt="Matcha" className="pt-card-logo" />
                  {isGhost && !isUp && <span className="pt-ghost-hint">{card.value}</span>}
                </div>
                <div className="pt-card-back">{face}</div>
              </div>
            </button>
          )
        })}
      </div>

      {phase === "won" && <div className="pt-won">cleared in {moves} moves 🎉</div>}
      {phase === "lost" && <div className="pt-won pt-lost">out of time ⏳<button className="pt-btn pt-btn-ghost" onClick={restart}>retry</button></div>}
    </div>
  )
}

function InfoModal({ mechanic, onClose }) {
  if (!mechanic) return null
  return (
    <div className="pt-modal-bg" onClick={onClose}>
      <div className="pt-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{mechanic.name}</h3>
        <p>{mechanic.info}</p>
        <button className="pt-btn pt-btn-primary" onClick={onClose}>got it</button>
      </div>
    </div>
  )
}

function FeedbackForm({ mechanic, result, onDone }) {
  const [fun, setFun] = useState(0)
  const [verdict, setVerdict] = useState("")
  const [difficulty, setDifficulty] = useState("")
  const [comment, setComment] = useState("")
  const [status, setStatus] = useState("idle")

  const submit = async () => {
    setStatus("sending")
    try {
      await addDoc(collection(db, "playtest_feedback"), {
        mechanic: mechanic.id, mechanicName: mechanic.name, kind: mechanic.kind,
        fun, verdict, difficulty, comment: comment.trim(),
        result: result?.result || "abandoned", moves: result?.moves ?? null, time: result?.time ?? null,
        userAgent: navigator.userAgent, timestamp: serverTimestamp(),
      })
      setStatus("done"); setTimeout(onDone, 900)
    } catch (e) { console.error(e); setStatus("error") }
  }

  return (
    <div className="pt-feedback">
      <h3>How did <span className="pt-accent">{mechanic.name}</span> feel?</h3>
      <label>Fun?</label>
      <div className="pt-stars">
        {[1, 2, 3, 4, 5].map((n) => <button key={n} className={`pt-star ${fun >= n ? "on" : ""}`} onClick={() => setFun(n)}>★</button>)}
      </div>
      <label>Keep it in the game?</label>
      <div className="pt-choices">
        {["keep", "tweak", "cut"].map((v) => <button key={v} className={`pt-chip ${verdict === v ? "on" : ""}`} onClick={() => setVerdict(v)}>{v}</button>)}
      </div>
      <label>Difficulty</label>
      <div className="pt-choices">
        {["too much", "just right", "too easy"].map((d) => <button key={d} className={`pt-chip ${difficulty === d ? "on" : ""}`} onClick={() => setDifficulty(d)}>{d}</button>)}
      </div>
      <label>Anything else? (optional)</label>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="thoughts, ideas…" />
      <div className="pt-fb-actions">
        <button className="pt-btn pt-btn-primary" onClick={submit} disabled={status === "sending" || status === "done"}>
          {status === "done" ? "✓ sent" : status === "sending" ? "sending…" : "submit & back"}
        </button>
        <button className="pt-btn pt-btn-ghost" onClick={onDone}>skip</button>
      </div>
      {status === "error" && <p className="pt-err">couldn't send — try again?</p>}
    </div>
  )
}

function Hub({ tried, onPick, onInfo }) {
  const groups = [
    ["Modifiers — the board fights back", "modifier"],
    ["Objectives — what 'winning' means", "objective"],
  ]
  return (
    <div className="pt-hub">
      {groups.map(([title, kind]) => (
        <section key={kind}>
          <h2>{title}</h2>
          <div className="pt-cards">
            {MECHANICS.filter((m) => m.kind === kind).map((m) => (
              <div key={m.id} className={`pt-mcard ${tried.has(m.id) ? "done" : ""}`}>
                <button className="pt-info" onClick={() => onInfo(m)} aria-label="what's this">?</button>
                <h4>{m.name} {tried.has(m.id) && <span className="pt-check">✓</span>}</h4>
                <p>{m.blurb}</p>
                <button className="pt-btn pt-btn-primary pt-play" onClick={() => onPick(m)}>{tried.has(m.id) ? "replay" : "try it"}</button>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

export default function Playtest() {
  const params = new URLSearchParams(window.location.search)
  const [unlocked, setUnlocked] = useState(() => params.get("key") === PLAYTEST_KEY || localStorage.getItem("playtest_unlocked") === "1")
  const [entry, setEntry] = useState("")
  const [view, setView] = useState("hub") // hub | play | feedback
  const [active, setActive] = useState(null)
  const [result, setResult] = useState(null)
  const [info, setInfo] = useState(null)
  const [tried, setTried] = useState(new Set())

  useEffect(() => { if (unlocked) localStorage.setItem("playtest_unlocked", "1") }, [unlocked])

  if (!unlocked) {
    return (
      <div className="pt-gate"><div className="pt-gate-box">
        <h2>Matcha · Playtest</h2>
        <p>Enter the access key to try the new mechanics.</p>
        <input type="password" value={entry} autoFocus onChange={(e) => setEntry(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && setUnlocked(entry === PLAYTEST_KEY)} placeholder="access key" />
        <button className="pt-btn pt-btn-primary" onClick={() => setUnlocked(entry === PLAYTEST_KEY)}>enter</button>
        {entry && entry !== PLAYTEST_KEY && <p className="pt-err">nope, try again</p>}
      </div></div>
    )
  }

  const pick = (m) => { setActive(m); setResult(null); setView("play"); setInfo(m) /* show context first */ }
  const finishRound = (r) => { setResult(r); setView("feedback") }
  const backToHub = () => { if (active) setTried((t) => new Set(t).add(active.id)); setView("hub"); setActive(null) }

  return (
    <div className="pt-page">
      <header className="pt-header">
        <h1>Matcha Lab <span className="pt-tag">PLAYTEST</span></h1>
        <p>Try each mechanic, then tell us how it felt. Tap <b>?</b> on any card for what it does. We're deciding what makes it into the mobile game.</p>
      </header>

      {view === "hub" && <Hub tried={tried} onPick={pick} onInfo={setInfo} />}

      {view === "play" && active && (
        <div className="pt-round">
          <div className="pt-round-top">
            <button className="pt-btn pt-btn-ghost" onClick={backToHub}>← all mechanics</button>
            <h2>{active.name} <button className="pt-info pt-info-sm" onClick={() => setInfo(active)}>?</button></h2>
            <button className="pt-btn pt-btn-ghost" onClick={() => finishRound(null)}>done / rate →</button>
          </div>
          <LabBoard key={active.id} cfg={active.cfg} onDone={finishRound} />
        </div>
      )}

      {view === "feedback" && active && <FeedbackForm mechanic={active} result={result} onDone={backToHub} />}

      <InfoModal mechanic={info} onClose={() => setInfo(null)} />
      <footer className="pt-footer">internal playtest · feedback recorded anonymously</footer>
    </div>
  )
}
